import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Coins, GripVertical } from "lucide-react";
import { toast } from "sonner";

const TRIGGERS = [
  ["on_order", "Bei Auftrag"],
  ["days_after_order", "X Tage nach Auftrag"],
  ["milestone", "Bei Meilenstein"],
  ["monthly_recurring", "Monatlich wiederkehrend"],
];

const empty = { name: "", description: "", installments: [], is_default: false };
const emptyInstallment = { label: "", percent: 50, fixed_amount: null, trigger: "on_order", days_offset: 0, milestone_label: "" };

export default function PaymentModelsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/payment-models")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      installments: form.installments.map(i => ({
        ...i,
        percent: i.percent != null && i.percent !== "" ? Number(i.percent) : null,
        fixed_amount: i.fixed_amount != null && i.fixed_amount !== "" ? Number(i.fixed_amount) : null,
        days_offset: Number(i.days_offset) || 0,
      })),
    };
    try {
      if (editing) await api.patch(`/payment-models/${editing.id}`, payload);
      else await api.post("/payment-models", payload);
      toast.success(editing ? "Aktualisiert" : "Angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Zahlungsmodell wirklich löschen?")) return;
    await api.delete(`/payment-models/${id}`); toast.success("Gelöscht"); load();
  };

  const addInstallment = () => setForm({ ...form, installments: [...form.installments, { ...emptyInstallment }] });
  const removeInstallment = (idx) => setForm({ ...form, installments: form.installments.filter((_, i) => i !== idx) });
  const updateInstallment = (idx, patch) => setForm({
    ...form, installments: form.installments.map((i, n) => n === idx ? { ...i, ...patch } : i)
  });

  return (
    <div className="space-y-6" data-testid="payment-models-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Zahlungsmodelle</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Zahlungspläne</h1>
          <p className="text-sm text-neutral-500 mt-2">Definiere wiederverwendbare Ratenpläne (50/50, Meilensteine, Retainer) und ordne sie Projekten zu — daraus generiert das System automatisch Ausgangsrechnungs-Entwürfe.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="add-pm-btn"><Plus className="h-4 w-4 mr-2" />Neues Zahlungsmodell</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Zahlungsmodell bearbeiten" : "Neues Zahlungsmodell"}</DialogTitle>
              <DialogDescription>Lege fest, wann und in welcher Höhe der Kunde zahlt.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. 50/50" data-testid="pm-name" /></div>
                <div className="flex items-center justify-between border rounded-lg px-4 py-2">
                  <Label className="m-0">Als Standard markieren</Label>
                  <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} />
                </div>
              </div>
              <div><Label>Beschreibung</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold">Raten / Teilrechnungen</div>
                  <Button size="sm" variant="outline" onClick={addInstallment} data-testid="pm-add-installment"><Plus className="h-3.5 w-3.5 mr-1" />Rate</Button>
                </div>
                {form.installments.length === 0 && <div className="text-sm text-neutral-500 text-center py-6 border border-dashed rounded-lg">Noch keine Raten — füge welche hinzu</div>}
                <div className="space-y-3">
                  {form.installments.map((inst, idx) => (
                    <div key={idx} className="border rounded-lg p-3 space-y-2" data-testid={`pm-installment-${idx}`}>
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-neutral-400" />
                        <Input className="flex-1" placeholder="Bezeichnung (z.B. Anzahlung)" value={inst.label} onChange={(e) => updateInstallment(idx, { label: e.target.value })} />
                        <Button size="icon" variant="ghost" onClick={() => removeInstallment(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">% oder Fixbetrag</Label>
                          <div className="flex gap-2">
                            <Input type="number" placeholder="%" value={inst.percent ?? ""} onChange={(e) => updateInstallment(idx, { percent: e.target.value, fixed_amount: null })} />
                            <Input type="number" placeholder="€" value={inst.fixed_amount ?? ""} onChange={(e) => updateInstallment(idx, { fixed_amount: e.target.value, percent: null })} />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Fälligkeit</Label>
                          <Select value={inst.trigger} onValueChange={(v) => updateInstallment(idx, { trigger: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{TRIGGERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      {inst.trigger === "days_after_order" && (
                        <div><Label className="text-xs">Tage nach Auftrag</Label><Input type="number" value={inst.days_offset} onChange={(e) => updateInstallment(idx, { days_offset: e.target.value })} /></div>
                      )}
                      {inst.trigger === "milestone" && (
                        <div><Label className="text-xs">Meilenstein</Label><Input value={inst.milestone_label || ""} onChange={(e) => updateInstallment(idx, { milestone_label: e.target.value })} placeholder="z.B. Abnahme, 50% Fortschritt" /></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="pm-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((pm) => {
          const sumPct = pm.installments.reduce((s, i) => s + (i.percent || 0), 0);
          const sumFixed = pm.installments.reduce((s, i) => s + (i.fixed_amount || 0), 0);
          return (
            <div key={pm.id} className="rounded-xl border border-neutral-200 bg-white p-5 flex flex-col" data-testid={`pm-card-${pm.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display font-semibold text-neutral-900 flex items-center gap-2">{pm.name}{pm.is_default && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Standard</span>}</div>
                  <div className="text-xs text-neutral-500 mt-1">{pm.description}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(pm); setForm({ ...pm }); setOpen(true); }} data-testid={`pm-edit-${pm.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(pm.id)} data-testid={`pm-delete-${pm.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
              <div className="space-y-1 mt-3 flex-1">
                {pm.installments.map((i, n) => (
                  <div key={n} className="flex justify-between text-sm py-1 border-b border-neutral-100 last:border-0">
                    <span className="text-neutral-700">{n + 1}. {i.label}</span>
                    <span className="text-neutral-900 font-medium tabular-nums">
                      {i.percent != null ? `${i.percent}%` : fmtCurrency(i.fixed_amount || 0)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-neutral-500 mt-3 pt-3 border-t border-neutral-100 flex items-center gap-2">
                <Coins className="h-3.5 w-3.5" />
                {sumPct > 0 ? `Summe ${sumPct}%` : sumFixed > 0 ? `Fix ${fmtCurrency(sumFixed)}` : "—"}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center text-neutral-500 py-12 border border-dashed rounded-xl">Noch keine Zahlungsmodelle</div>}
      </div>
    </div>
  );
}
